import { useElementSize, useFullscreen, useMergedRef } from "@mantine/hooks"
import useGlobalVolume from "app/core/hooks/useGlobalVolume"
import { useIsPublic } from "app/core/hooks/useIsPublic"
import VideoElement from "app/player/components/VideoElement"
import useBufferedAmount from "app/player/hooks/useBufferedAmount"
import useHideControls from "app/player/hooks/useHideControls"
import usePlaybackRate from "app/player/hooks/usePlaybackRate"
import usePlaybackToggle from "app/player/hooks/usePlaybackToggle"
import useProgressValueChange from "app/player/hooks/useProgressValueChange"
import {
  isFinishedAtom,
  isLoopEnabledAtom,
  isPlayingAtom,
} from "app/player/state"
import skipMediaBy from "app/player/utils/skipMediaBy"
import useVideoVariant from "app/video-slides/hooks/useVideoVariant"
import sortVariants from "app/video-slides/utils/sortVariants"
import PlayerInitialPlayButton from "app/video-slides/components/player/PlayerInitialPlayButton"
import VideoPlayerControls from "app/video-slides/components/player/VideoPlayerControls"
import { FileVariantType } from "db/enums"
import { Provider, useAtomValue, useSetAtom } from "jotai"
import { AnimatePresence } from "framer-motion"
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react"
import { FileWithVariants } from "utils/files/types"
import {
  exitElementFullscreen,
  getFullscreenElement,
  requestElementFullscreen,
} from "web/src/slides/hooks/useCompactNavigation"
import { applyMediaVolume } from "app/player/utils/applyMediaVolume"
import { isVideoPlayerShortHeight } from "app/video-slides/consts"

type Props = {
  file: FileWithVariants
  onTimeUpdate?: () => void
  autoPlay?: boolean
  loop?: boolean
  /** When false, hides the overlay UI (play button + scrubber). Defaults to true. */
  controls?: boolean
}

const VideoPlayerInner = forwardRef<HTMLVideoElement, Props>(
  function VideoPlayerInner(
    { file, onTimeUpdate, autoPlay, loop, controls = true },
    ref
  ) {
    const videoSlideRef = useRef<HTMLDivElement | null>(null)
    const videoElementRef = useRef<HTMLVideoElement | null>(null)
    const qualityChangeAbortRef = useRef<AbortController | null>(null)
    const { ref: fullscreenRef } = useFullscreen()
    const { ref: sizeRef, height: playerHeight } = useElementSize()
    const videoSlideMergedRef = useMergedRef(
      fullscreenRef,
      videoSlideRef,
      sizeRef
    )
    const controlSize = isVideoPlayerShortHeight(playerHeight) ? "sm" : "md"
    const fullscreenPrevStateRef = useRef<Element | null>(null)
    const [isBigPlayButtonVisible, setBigPlayButtonVisible] = useState(true)
    const toggleVideoPlayback = usePlaybackToggle(videoElementRef, {
      rootRef: videoSlideRef,
    })
    const { selectedQuality, url, setQuality } = useVideoVariant(file)
    const isPublicLesson = useIsPublic()
    const bufferedAmount = useBufferedAmount(videoElementRef, url)

    const [selectedPlaybackSpeed, setPlaybackSpeed] =
      usePlaybackRate(videoElementRef)
    const [volume, setVolume] = useGlobalVolume()

    const {
      isVisible: controlsVisible,
      props: rootProps,
      isTouch,
      hideControls,
    } = useHideControls()

    const onProgressValueChange = useProgressValueChange(videoElementRef)

    const setLoopEnabled = useSetAtom(isLoopEnabledAtom)
    useEffect(() => {
      setLoopEnabled(!!loop)
    }, [loop, setLoopEnabled])

    useEffect(() => {
      const videoElement = videoElementRef.current
      if (!videoElement) return

      const applyVolume = () => {
        applyMediaVolume(videoElement, volume)
      }

      applyVolume()
      videoElement.addEventListener("loadeddata", applyVolume)
      return () => videoElement.removeEventListener("loadeddata", applyVolume)
    }, [volume, url])

    const isFinished = useAtomValue(isFinishedAtom)
    const setIsPlayingAtom = useSetAtom(isPlayingAtom)

    useEffect(() => {
      return () => {
        qualityChangeAbortRef.current?.abort()
      }
    }, [])

    const handleQualityChange = useCallback(
      (option: FileVariantType) => {
        const videoElement = videoElementRef.current
        if (!videoElement) return

        const timeToRestore = videoElement.currentTime
        const shouldResumePlayback = !videoElement.paused

        qualityChangeAbortRef.current?.abort()
        const abortController = new AbortController()
        qualityChangeAbortRef.current = abortController

        videoElement.pause()
        setQuality(option)
        setIsPlayingAtom(false)

        videoElement.addEventListener(
          "loadeddata",
          () => {
            videoElement.currentTime = timeToRestore
            if (shouldResumePlayback) {
              videoElement.play().catch(console.error)
              setIsPlayingAtom(true)
            }
          },
          { once: true, signal: abortController.signal }
        )
      },
      [setQuality, setIsPlayingAtom]
    )

    const sortedVariants = useMemo(
      () => (file.variants ? sortVariants([...file.variants]) : []),
      [file.variants]
    )

    useImperativeHandle(ref, () => videoElementRef.current!, [])

    const handleSkip = useCallback((direction: "back" | "forward") => {
      const videoElement = videoElementRef.current
      if (!videoElement) return
      skipMediaBy(videoElement, direction)
    }, [])

    const handleSkipBack = useCallback(() => {
      handleSkip("back")
    }, [handleSkip])

    const handleSkipForward = useCallback(() => {
      handleSkip("forward")
    }, [handleSkip])

    const handleFullscreenClick = useCallback(async () => {
      const videoContainer = videoSlideRef.current
      if (!videoContainer) return

      const currentFullscreenElement = getFullscreenElement()
      const isOwnFullscreen =
        currentFullscreenElement === videoContainer ||
        (currentFullscreenElement instanceof HTMLVideoElement &&
          videoContainer.contains(currentFullscreenElement))

      if (isOwnFullscreen) {
        const previousFullscreenElement = fullscreenPrevStateRef.current
        fullscreenPrevStateRef.current = null
        await exitElementFullscreen()
        if (previousFullscreenElement) {
          await requestElementFullscreen(previousFullscreenElement)
        }
        return
      }

      if (currentFullscreenElement) {
        fullscreenPrevStateRef.current = currentFullscreenElement
        await exitElementFullscreen()
      } else {
        fullscreenPrevStateRef.current = null
      }

      await requestElementFullscreen(videoContainer)
    }, [])

    const handleDoubleClick = useCallback(() => {
      void handleFullscreenClick()
    }, [handleFullscreenClick])

    const handleVideoClick = useCallback(() => {
      if (isTouch) return
      if (isFinished) return toggleVideoPlayback("REPLAY")
      toggleVideoPlayback()
    }, [isTouch, isFinished, toggleVideoPlayback])

    const handlePlay = useCallback(() => {
      setBigPlayButtonVisible(false)
    }, [])

    const handleReplayClick = useCallback(() => {
      toggleVideoPlayback("REPLAY")
    }, [toggleVideoPlayback])

    const handleInitialPlayClick = useCallback(() => {
      const elm = videoElementRef.current
      if (!elm) return
      elm.currentTime = 0
      if (isTouch) {
        hideControls()
      }
      elm.play().catch(console.error)
    }, [isTouch, hideControls])

    return (
      <div
        {...rootProps}
        ref={videoSlideMergedRef}
        className="relative size-full overflow-hidden bg-black"
        onContextMenu={(e) => e.preventDefault()}
      >
        <div className="absolute inset-0">
          <VideoElement
            ref={videoElementRef}
            className="absolute inset-0 size-full object-contain"
            crossOrigin="anonymous"
            src={url ?? ""}
            onDoubleClick={handleDoubleClick}
            onClick={handleVideoClick}
            autoPlay={autoPlay}
            onPlay={handlePlay}
            onTimeUpdate={onTimeUpdate}
            playsInline={true}
          />

          {controls ? (
            <>
              <div className="pointer-events-none absolute inset-0">
                <AnimatePresence>
                  {isBigPlayButtonVisible && (
                    <PlayerInitialPlayButton
                      key="initial-play-button"
                      size={controlSize}
                      className="pointer-events-auto absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
                      onClick={handleInitialPlayClick}
                    />
                  )}
                </AnimatePresence>
              </div>

              {!isBigPlayButtonVisible && (
                <VideoPlayerControls
                  controlsVisible={controlsVisible}
                  volume={volume}
                  onVolumeChange={setVolume}
                  onProgressValueChange={onProgressValueChange}
                  bufferedAmount={bufferedAmount}
                  onTogglePlayback={toggleVideoPlayback}
                  onReplayClick={handleReplayClick}
                  onSkipBack={handleSkipBack}
                  onSkipForward={handleSkipForward}
                  onFullscreenClick={handleFullscreenClick}
                  selectedPlaybackSpeed={selectedPlaybackSpeed}
                  setPlaybackSpeed={setPlaybackSpeed}
                  isPublicLesson={isPublicLesson}
                  videoId={file.id}
                  selectedQuality={selectedQuality}
                  sortedVariants={sortedVariants}
                  onQualityChange={handleQualityChange}
                  size={controlSize}
                />
              )}
            </>
          ) : null}
        </div>
      </div>
    )
  }
)

/**
 * Per-instance Jotai store so playback/loop/time atoms don't clash when
 * multiple VideoPlayers are mounted (e.g. custom slides).
 */
export default forwardRef<HTMLVideoElement, Props>(
  function VideoPlayer(props, ref) {
    return (
      <Provider>
        <VideoPlayerInner {...props} ref={ref} />
      </Provider>
    )
  }
)
